import { Sparkles } from "lucide-react"
import { motion, useAnimation } from "motion/react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useStore } from "react-redux"
import useGetPageId from "../../hooks/use-get-pageid"
import { screenshotStorage } from "../../services/screenshotStorage"
import { RootState, useAppSelector } from "../../store"
import { getFullPageScreenshotId, getPageThumbnail } from "../../store/uiSlice"
import WebsiteWireframe from "../WebsiteWireframe"


interface DotData {
    id: string
    x: number
    y: number
    delay: number
}

const useCurrentPageScreenshotId = () => {
    const store = useStore<RootState>();

    const getCurrentPageScreenshotId = () => {
        return getFullPageScreenshotId(store.getState())
    }

    return getCurrentPageScreenshotId;
};

export default function ScrollingScreenshot() {
    const containerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null) // Ref for the motion.div containing the image
    const controls = useAnimation()
    const [dimensionsReady, setDimensionsReady] = useState(false)
    const [dots, setDots] = useState<DotData[]>([]) // State to hold the dots
    const getCurrentPageScreenshotId = useCurrentPageScreenshotId();
    const { pageId } = useGetPageId();
    const pageThumbnail = useAppSelector(getPageThumbnail);
    const [screenshot, setScreenshot] = useState<string | null>(null)
    const [isCheckingImage, setIsCheckingImage] = useState<boolean>(true)
    const IMAGE_SRC = useMemo(() => `https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/uploads/design-review/pages/${pageId}/full-page-thumbnail.webp`, [pageId]);


    useEffect(() => {
        let isCancelled = false

        const preloadImage = (url: string, timeoutMs = 10000): Promise<boolean> => {
            return new Promise((resolve) => {
                const img = new Image()
                let settled = false
                const done = (ok: boolean) => {
                    if (settled) return
                    settled = true
                    img.onload = null
                    img.onerror = null
                    clearTimeout(timer)
                    resolve(ok)
                }
                const timer = setTimeout(() => done(false), timeoutMs)
                img.onload = () => done(true)
                img.onerror = () => done(false)
                img.src = url
            })
        }

        const headOk = async (url: string): Promise<boolean | null> => {
            // Returns true if HTTP 200..299, false if non-OK, null if not determinable (e.g., CORS/opaque)
            try {
                const res = await fetch(url, { method: 'HEAD', cache: 'no-cache' })
                return res.ok
            } catch {
                return null
            }
        }

        const isImageUsable = async (url: string): Promise<boolean> => {
            if (!url) return false
            // For blob/data URLs, HEAD won't work; rely on preload
            if (url.startsWith('blob:') || url.startsWith('data:')) {
                return await preloadImage(url)
            }
            const ok = await headOk(url)
            if (ok === false) return false
            // If ok === true or null (opaque), still ensure the browser can decode it
            return await preloadImage(url)
        }

        const resolveScreenshot = async () => {
            setIsCheckingImage(true)
            setScreenshot(null)
            try {
                // Build candidates in priority order
                const candidates: Array<() => Promise<string | null>> = []
                const currentPageScreenshotId = getCurrentPageScreenshotId()
                if (currentPageScreenshotId) {
                    candidates.push(async () => {
                        try {
                            return await screenshotStorage.getScreenshotBlobUrl(currentPageScreenshotId)
                        } catch {
                            return null
                        }
                    })
                }
                if (pageThumbnail) {
                    candidates.push(async () => pageThumbnail)
                }
                candidates.push(async () => IMAGE_SRC)

                for (const getUrl of candidates) {
                    const url = await getUrl()
                    if (!url) continue
                    const usable = await isImageUsable(url)
                    if (usable) {
                        if (!isCancelled) {
                            setScreenshot(url)
                        }
                        break
                    }
                }
            } finally {
                if (!isCancelled) setIsCheckingImage(false)
            }
        }

        resolveScreenshot()
        return () => {
            isCancelled = true
        }
    }, [getCurrentPageScreenshotId, IMAGE_SRC, pageThumbnail])

    // Function to generate random dots for the current visible screen
    const generateRandomDots = (currentImageScrollY: number, numDots = 3) => {
        if (!containerRef.current) return

        const containerWidth = containerRef.current.clientWidth
        const containerHeight = containerRef.current.clientHeight
        const newDots: DotData[] = []
        let cumulativeDelay = 0.1 // Base delay for the first dot

        for (let i = 0; i < numDots; i++) {
            const randomX = Math.random() * (containerWidth - 16) // Adjusted bounds
            const randomY = Math.random() * (containerHeight - 16) // Adjusted bounds

            // Add a random offset to the cumulative delay for staggered appearance
            cumulativeDelay += Math.random() * 0.4 + 0.1 // Random delay between 0.1s and 0.5s

            newDots.push({
                id: `${Date.now()}-${i}-${Math.random()}`, // Unique ID
                x: randomX,
                y: currentImageScrollY + randomY, // Dot's Y position relative to the top of the *entire image*
                delay: cumulativeDelay, // Pass the calculated delay
            })
        }
        setDots((prevDots) => [...prevDots, ...newDots])
    }

    useEffect(() => {
        const measureDimensions = () => {
            if (containerRef.current && contentRef.current) {
                setDimensionsReady(true)
            }
        }

        const timeoutId = setTimeout(measureDimensions, 100)
        window.addEventListener("resize", measureDimensions)

        return () => {
            clearTimeout(timeoutId)
            window.removeEventListener("resize", measureDimensions)
        }
    }, [])

    // Effect to generate initial dots as soon as dimensions are ready
    useEffect(() => {
        if (dimensionsReady) {
            generateRandomDots(0) // Generate dots for the very first screen (scrollY 0)
        }
    }, [dimensionsReady]) // Only run when dimensionsReady changes

    useEffect(() => {
        const animateScroll = async () => {
            if (!containerRef.current || !contentRef.current || !dimensionsReady) {
                return
            }

            // Add initial 4-second delay *before* the first scroll animation starts
            await new Promise((resolve) => setTimeout(resolve, 4000))

            // Re-check refs after the delay in case component was unmounted
            if (!containerRef.current || !contentRef.current) {
                return
            }

            const containerHeight = containerRef.current.clientHeight
            const contentHeight = contentRef.current.clientHeight
            const scrollableHeight = contentHeight - containerHeight

            if (scrollableHeight <= 0) {
                return
            }

            let currentScrollY = 0
            const scrollStep = containerHeight

            const numSteps = Math.ceil(scrollableHeight / scrollStep)

            for (let i = 0; i < numSteps; i++) {

                // Pause for 4 seconds *before* the next scroll, if it's not the very first step
                if (i > 0) {
                    await new Promise((resolve) => setTimeout(resolve, 8000))

                    // Re-check refs after the delay in case component was unmounted
                    if (!containerRef.current || !contentRef.current) {
                        return
                    }
                }

                const targetY = Math.min(currentScrollY + scrollStep, scrollableHeight)
                const duration = 4 // seconds

                await controls.start({
                    y: -targetY,
                    transition: { duration: duration, ease: "easeInOut" },
                })

                currentScrollY = targetY

                // Calculate remaining scrollable height after the current step
                const remainingScroll = scrollableHeight - currentScrollY
                // Always generate dots for the screen that just scrolled into view.
                // This ensures dots always appear on a new screen, including the "second-to-last" full screen.
                generateRandomDots(currentScrollY)

                if (currentScrollY < scrollableHeight && remainingScroll < containerHeight / 2) {
                    // No further dot generation needed for the tiny last segment.
                    // The loop will continue to ensure the image fully scrolls, but no new dots will be made.
                }
            }
        }

        if (dimensionsReady) {
            animateScroll()
        }
    }, [dimensionsReady, controls])


    return (

        <div
            ref={containerRef}
            className="w-full max-w-md aspect-video relative overflow-hidden rounded-lg shadow-lg bg-gray-200 dark:bg-gray-800"
        >
            {isCheckingImage ? (
                <div className="absolute inset-0 w-full h-full">
                    <WebsiteWireframe />
                </div>
            ) : screenshot ? (
                <motion.div
                    ref={contentRef}
                    className="absolute top-0 left-0 w-full z-10"
                    animate={controls}
                >
                    <img
                        src={screenshot}
                        alt="Tall scrolling image"
                        className="w-full h-auto"
                    />
                    {dots.map((dot) => (
                        <Dot key={dot.id} x={dot.x} y={dot.y} delay={dot.delay} />
                    ))}
                </motion.div>
            ) : (
                <div className="absolute inset-0 w-full h-full">
                    <WebsiteWireframe />
                </div>
            )}
        </div>
    )
}

interface DotProps {
    x: number // X position relative to the parent (motion.div)
    y: number // Y position relative to the parent (motion.div)
    color?: string
    size?: string
    delay?: number // Delay in seconds before the dot appears
}

export const Dot: React.FC<DotProps> = ({ x, y, delay = 0 }) => {
    return (

        <motion.div
            className={`absolute rounded-full`}
            style={{
                left: `${x}px`,
                top: `${y}px`,
                zIndex: 20, // Ensure dots are above the image
            }}
            initial={{ opacity: 0, scale: 0.8 }} // Start invisible and slightly smaller
            animate={{
                opacity: 1,
                scale: 1,
                boxShadow: [
                    '0px 0px 8px rgba(109, 93, 243, 1)',
                    '0px 0px 6px rgba(109, 93, 243, 0.25)',
                    '0px 0px 8px rgba(109, 93, 243, 1)',
                ]
            }}
            transition={{
                delay: delay, // Use the provided delay
                duration: 0.3, // Quick appearance animation
                ease: "easeOut",
                boxShadow: {
                    duration: 1,
                    ease: 'easeInOut',
                    repeat: Infinity,
                    repeatType: 'mirror',
                    repeatDelay: 0.5,
                }
            }}
        >
            <div
                className="bg-tertiary relative flex h-4 w-4 items-center justify-center overflow-hidden rounded-[15%_50%_50%_50%] p-1 outline outline-1 outline-white"
            >
                <span className='absolute bg-[#B07DF6] rounded-xl -bottom-2 h-6 w-8 blur' style={{ zIndex: -1 }} />
                <span className='absolute bg-[#D5BCF8] rounded-xl -bottom-2.5 h-4 w-6 blur-sm z-10' style={{ zIndex: -1 }} />
                <span className="relative z-10 text-xs font-normal text-white">
                    <Sparkles size={8} />
                </span>
            </div>
        </motion.div>
    )
}


